import { useState, useEffect, useRef } from "react";
import imgPortrait from '../img/photo_png.png';
import Header from './Header';
import { TbChevronsDown } from "react-icons/tb";
import ForYou from './ForYou';


export default function Title() {
  const [trail, setTrail] = useState([]);
  // State pour gérer la taille de l'écran de façon responsive
  const [screenSize, setScreenSize] = useState({ 
    width: typeof window !== 'undefined' ? window.innerWidth : 1024, 
    height: typeof window !== 'undefined' ? window.innerHeight : 768 
  });

  // Vérifier si on est sur mobile
  const isMobile = screenSize.width < 768;

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      // Utilise scrollIntoView avec block: "start" pour respecter les CSS scroll-margin-top
      targetElement.scrollIntoView({ 
        behavior: "smooth",
      });
    }
  };
  
  // Références pour la position cible et la position actuelle interpolée
  const targetMouse = useRef({ x: screenSize.width / 2, y: screenSize.height / 2 });
  const currentMouse = useRef({ x: screenSize.width / 2, y: screenSize.height / 2 });
  
  // Ref du conteneur et état pour son offset
  const containerRef = useRef(null);
  const [offset, setOffset] = useState({ top: 0, left: 0 });
  
  // Au montage, on calcule l'offset du conteneur
  useEffect(() => {
    const updateOffset = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setOffset({ top: rect.top + window.scrollY, left: rect.left + window.scrollX });
      }
    };

    updateOffset();
    
    // Gestion du redimensionnement d'écran
    const handleResize = () => {
      const newSize = { width: window.innerWidth, height: window.innerHeight };
      setScreenSize(newSize);
      targetMouse.current = { x: newSize.width / 2, y: newSize.height / 2 };
      currentMouse.current = { x: newSize.width / 2, y: newSize.height / 2 };
      updateOffset();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', updateOffset);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updateOffset);
    };
  }, []);
  
  // Fonction de lerp pour interpoler de manière fluide
  const lerp = (start, end, amt) => (1 - amt) * start + amt * end;
  
  // Màj de la position cible au déplacement de la souris (désactivé sur mobile)
  useEffect(() => {
    if (isMobile) return;
    
    const handleMouseMove = (e) => {
      targetMouse.current = { x: e.pageX, y: e.pageY };
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isMobile]);
  
  // Boucle d'animation pour interpoler currentMouse vers targetMouse et mettre à jour le ruban (désactivé sur mobile)
  useEffect(() => {
    if (isMobile) {
      setTrail([]);
      return;
    }
    
    let animationFrameId;
    const animate = () => {
      currentMouse.current = {
        x: lerp(currentMouse.current.x, targetMouse.current.x, 0.1),
        y: lerp(currentMouse.current.y, targetMouse.current.y, 0.1)
      };
      setTrail(prev => {
        const newTrail = [...prev, { ...currentMouse.current }];
        if (newTrail.length > 20) newTrail.shift();
        return newTrail;
      });
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animationFrameId);
  }, [isMobile]);
  
  return (
    <div ref={containerRef} className="relative min-h-screen w-full">
      {/* SVG en arrière-plan pour dessiner le ruban (masqué sur mobile) */}
      {!isMobile && (
        <svg
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {trail.map((point, index) => {
            if (index === 0) return null;
            const prev = trail[index - 1];
            // L'opacité varie de faible pour les points anciens à forte pour les plus récents
            const opacity = (index + 1) / trail.length;
            return (
              <line
                key={index}
                x1={prev.x - offset.left}
                y1={prev.y - offset.top}
                x2={point.x - offset.left}
                y2={point.y - offset.top}
                stroke={`rgba(0,0,0,${opacity})`}
                strokeWidth="6"
                strokeLinecap="round"
              />
            );
          })}
        </svg>
      )}

      {/* Contenu principal */}
      <div className="min-h-screen w-full bg-white pt-16 md:pt-20">
        <Header/>
        
        {/* Section principale avec layout en deux colonnes */}
        <div className=" w-full flex  ">
          <div className="w-full max-w-7xl mx-8 md:mx-10 lg:mx-10 xl:mx-auto px-4 sm:px-8 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-1">
              
              {/* Colonne gauche - Texte et boutons */}
              <div className="w-full lg:w-1/2 text-center lg:text-left">
                <div className="mb-6">
                  <p className="text-4xl lg:text-4xl xl:text-5xl font-light  text-gray-600 mb-4 font-inria ">
                    Je crée des <span className="lg:text-4xl xl:text-5xl  text-purple-500 font-semibold">sites web</span> et <span className="lg:text-4xl xl:text-5xl  text-purple-500 font-semibold">applications</span> personnalisés
                  </p>
                </div>
                
                <ForYou className="mx-[300px] px-[300px] lg:mt-8"/>
                
                <div className="mb-8 mt-10">
                  <p className="text-lg text-gray-700 mb-2 mr-0 md:mr-10 xl:mr-4">
                  Vous êtes un entrepreneur, une TPE/PME&nbsp;? Vous êtes au bon endroit&nbsp;!
                  </p>
      
                </div>
                
                {/* Boutons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a
                    href="#Contact"
                    className="cursor-pointer bg-purple-500 hover:bg-purple-600 text-white font-semibold text-lg px-8 py-3 rounded-full transition duration-300 shadow-lg hover:shadow-xl z-20"
                  >
                    Contact
                  </a>
                  <a
                    href="#Projets"
                    onClick={(e) => handleSmoothScroll(e, "Projets")}
                    className="cursor-pointer border-2 border-gray-700 text-gray-700 hover:border-purple-600 hover:text-purple-600 hover:shadow-xl font-semibold text-lg px-8 py-3 rounded-full transition duration-300 z-20"
                  >
                    Projets
                  </a>
                </div>
              </div>
              
              <div className="relative flex flex-col items-center">
  {/* 1) Le blob SVG */}
  <svg
    viewBox="0 0 200 200"
    className="md:block hidden absolute rotate-45 scale-[3] top-[300px] left-[100px] lg:top-[400px] lg:left-[250px] xl:top-[400px] xl:left-[200px] w-[500px] h-[500px] -translate-x-1/2 -translate-y-1/2 opacity-90"
    xmlns="http://www.w3.org/2000/svg"
  >
<path fill="#A259FF" d="M53.2,-34.2C63.7,-29.3,63.2,-7.5,55.6,7.6C48.1,22.7,33.6,31.2,21.4,32.5C9.2,33.8,-0.8,28,-13.1,23.5C-25.4,19,-40,15.7,-45.9,5.9C-51.8,-3.9,-48.8,-20.3,-39.7,-24.8C-30.6,-29.4,-15.3,-22.1,3,-24.5C21.4,-26.9,42.8,-39,53.2,-34.2Z" transform="translate(100 100)" />  </svg>

  {/* 2) Ton portrait */}
  <div className="md:block hidden relative mt-10 z-10 w-80 h-80 sm:w-96 sm:h-96 lg:w-[28rem] lg:h-[28rem] overflow-hidden border-4 border-slate-500 shadow-xl">
    <img
      src={imgPortrait}
      alt="Portrait"
      className="w-full h-full object-cover"
      decoding="async"
    />
  </div>
  <div className="flex justify-center items-center gap-2">
    <span className="text-center text-white font-inria font-light text-2xl z-10 ">by </span><p className="text-center text-white font-inria font-bold text-3xl z-10 "> Gautier VASSE</p>
  </div>
  
</div>
            </div>
          </div>
        </div>
        
        {/* Flèche pour faire défiler vers le bas */}
        <div className="flex justify-center items-center pb-8 sm:pb-12 md:pb-8" style={{ scrollBehavior: "smooth" }}>
          <a
            href="#qui-suis-je"
            onClick={(e) => handleSmoothScroll(e, "qui-suis-je")}
            className="text-4xl p-4 text-center rounded-full"
          >
            <TbChevronsDown className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 text-gray-700 mx-auto animate-bounce" />
          </a>
        </div>
      </div>
    </div>
  );
}